// Telemetry collector — kumpulin info hardware device untuk dashboard monitoring.
//
// Sumber data:
//   - performance.memory: Chrome only, heap stats JS (proxy untuk RAM usage).
//     Tidak ideal (heap != device RAM), tapi yang available di browser.
//   - navigator.deviceMemory: rough GB estimate device (Chrome only).
//   - navigator.storage.estimate(): quota & usage IndexedDB (proxy untuk storage).
//   - window.screen: resolusi fisik display.
//   - performance.now(): uptime sejak app boot (approximation).
//   - navigator.userAgent: parse untuk OS + model.

import { detectDevice } from "./device.js";

interface MemoryInfo {
  jsHeapSizeLimit?: number;
  totalJSHeapSize?: number;
  usedJSHeapSize?: number;
}

interface PerformanceWithMemory extends Performance {
  memory?: MemoryInfo;
}

interface NavigatorWithMemory extends Navigator {
  deviceMemory?: number; // GB, rough estimate
}

const APP_BOOT_TIMESTAMP = Date.now();
const APP_VERSION = "0.1.0";

export interface TelemetryPayload {
  ram_total_mb?: number;
  ram_available_mb?: number;
  storage_total_mb?: number;
  storage_available_mb?: number;
  /** Total bytes media di IndexedDB cache player (subset dari storage). */
  cache_bytes?: number;
  /** Jumlah media file di IndexedDB cache. */
  cache_count?: number;
  uptime_seconds?: number;
  resolution?: string;
  app_version?: string;
  os_version?: string;
  hardware_version?: string;
  /** player_type asli hasil deteksi device (android/firetv/tizen/webos/windows/chrome/web). */
  player_type?: string;
  /** Kelas device untuk info dashboard (tv/tablet/phone/desktop/kiosk). */
  device_class?: string;
}

export async function collectTelemetry(): Promise<TelemetryPayload> {
  const dev = detectDevice();
  const payload: TelemetryPayload = {
    uptime_seconds: Math.floor((Date.now() - APP_BOOT_TIMESTAMP) / 1000),
    resolution: `${window.screen.width}x${window.screen.height}`,
    app_version: APP_VERSION,
    os_version: detectOs(),
    hardware_version: detectHardware(),
    player_type: dev.playerType,
    device_class: dev.deviceClass,
  };

  // RAM: pakai navigator.deviceMemory (kalau ada) sebagai total, dan
  // performance.memory.usedJSHeapSize sebagai pendekatan used.
  const nav = navigator as NavigatorWithMemory;
  if (typeof nav.deviceMemory === "number" && nav.deviceMemory > 0) {
    payload.ram_total_mb = nav.deviceMemory * 1024;
  }
  const perf = performance as PerformanceWithMemory;
  if (perf.memory) {
    if (perf.memory.jsHeapSizeLimit && !payload.ram_total_mb) {
      // Fallback: pakai heap limit JS sebagai "total RAM" available untuk app.
      // (Bukan total fisik device, tapi yang relevan untuk player.)
      payload.ram_total_mb = Math.round(perf.memory.jsHeapSizeLimit / (1024 * 1024));
    }
    if (perf.memory.usedJSHeapSize && payload.ram_total_mb) {
      const usedMb = Math.round(perf.memory.usedJSHeapSize / (1024 * 1024));
      payload.ram_available_mb = Math.max(0, payload.ram_total_mb - usedMb);
    }
  }

  // Storage via Storage API.
  if (navigator.storage?.estimate) {
    try {
      const est = await navigator.storage.estimate();
      if (est.quota) payload.storage_total_mb = Math.round(est.quota / (1024 * 1024));
      if (est.quota && est.usage != null) {
        payload.storage_available_mb = Math.round(
          (est.quota - est.usage) / (1024 * 1024),
        );
      }
    } catch {
      // ignore
    }
  }

  // Cache stats — dari IndexedDB media store.
  try {
    const { getCacheSize } = await import("./media-cache.js");
    const size = await getCacheSize();
    payload.cache_bytes = size.bytes;
    payload.cache_count = size.count;
  } catch {
    // ignore — media-cache might not be initialized
  }

  return payload;
}

function detectOs(): string {
  const ua = navigator.userAgent;
  const platform = navigator.platform;
  // Common patterns
  const androidMatch = ua.match(/Android (\d+(?:\.\d+)?)/);
  if (androidMatch) return `Android ${androidMatch[1]}`;
  const iosMatch = ua.match(/OS (\d+(?:_\d+)+) like Mac OS X/);
  if (iosMatch) return `iOS ${iosMatch[1]!.replace(/_/g, ".")}`;
  if (/Windows/.test(ua)) return "Windows";
  if (/Mac OS X/.test(ua)) return "macOS";
  if (/Linux/.test(ua)) return "Linux";
  return platform.slice(0, 50);
}

function detectHardware(): string {
  const ua = navigator.userAgent;
  // Android: "(Linux; Android 14; Pixel 7) AppleWebKit..."
  const androidModel = ua.match(/\bAndroid [^;]+;\s*([^)]+)\)/);
  if (androidModel?.[1]) return androidModel[1].trim().slice(0, 100);
  // iOS: iPhone / iPad
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  // Fallback: browser+vendor
  return `${navigator.vendor || "unknown"} ${navigator.platform}`.trim().slice(0, 100);
}
